import ClearIcon from "@mui/icons-material/Clear";
import { Box, IconButton, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { extensionFromName, FullScreenDocumentPreview } from "components/Document/shared";
import { useCallback, useState, type ReactNode } from "react";

import { formatScoutFileHoverMetadata } from "../scoutAttachmentLimits";
import { ScoutFileThumbnailContent } from "./ScoutFileThumbnailContent";

const THUMBNAIL_WIDTH_PX = 112;
const THUMBNAIL_HEIGHT_PX = 144;
const SKELETON_LINE_WIDTHS = [0.92, 0.72, 0.56, 0.8] as const;

export type ScoutFileThumbnailCardProps = {
  filename: string;
  mediaType?: string;
  sizeBytes?: number;
  thumbnailUrl?: string;
  signedDownloadUrl?: string;
  previewFileUrl?: string;
  isLoading?: boolean;
  onRemove?: () => void;
  footer?: ReactNode;
};

function ScoutFileThumbnailSkeleton() {
  return (
    <Stack spacing={0.75} sx={{ height: "100%", justifyContent: "center", px: 1.25, py: 1.5 }}>
      {SKELETON_LINE_WIDTHS.map((width, index) => (
        <Skeleton
          key={`scout-file-skeleton-${index}`}
          variant="rounded"
          height={8}
          sx={{ width: `${width * 100}%`, borderRadius: 999 }}
        />
      ))}
    </Stack>
  );
}

export function ScoutFileThumbnailCard({
  filename,
  mediaType,
  sizeBytes,
  thumbnailUrl,
  signedDownloadUrl,
  previewFileUrl,
  isLoading = false,
  onRemove,
  footer,
}: ScoutFileThumbnailCardProps) {
  const theme = useTheme();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const fileExtension = extensionFromName(filename);
  const resolvedPreviewFileUrl = previewFileUrl ?? signedDownloadUrl;
  const canOpenPreview = resolvedPreviewFileUrl !== undefined && !isLoading;
  const hoverMetadata = formatScoutFileHoverMetadata({ mediaType, sizeBytes });
  const showHoverDetails = isHovered && !isLoading;
  const showRemoveButton = Boolean(onRemove) && showHoverDetails;

  const openPreview = useCallback(() => {
    if (!canOpenPreview || !resolvedPreviewFileUrl) {
      return;
    }
    setIsPreviewOpen(true);
  }, [canOpenPreview, resolvedPreviewFileUrl]);

  return (
    <Stack spacing={0.5} sx={{ flexShrink: 0, width: THUMBNAIL_WIDTH_PX }}>
      <Box
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={canOpenPreview ? openPreview : undefined}
        sx={{
          position: "relative",
          width: THUMBNAIL_WIDTH_PX,
          height: THUMBNAIL_HEIGHT_PX,
          borderRadius: 1.5,
          overflow: "hidden",
          border: 1,
          borderColor: "divider",
          bgcolor: alpha(theme.palette.text.primary, 0.04),
          cursor: canOpenPreview ? "pointer" : "default",
        }}
      >
        {isLoading && !thumbnailUrl ? (
          <ScoutFileThumbnailSkeleton />
        ) : (
          <ScoutFileThumbnailContent thumbnailUrl={thumbnailUrl} fileExtension={fileExtension} />
        )}

        {showHoverDetails ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.common.black, 0.58),
              px: 1,
              py: 1.25,
              gap: 0.5,
            }}
          >
            <Typography
              variant="caption"
              color="common.white"
              textAlign="center"
              sx={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 3,
                overflow: "hidden",
                wordBreak: "break-word",
                fontWeight: 600,
              }}
            >
              {filename}
            </Typography>
            {hoverMetadata ? (
              <Typography variant="caption" color="common.white" textAlign="center">
                {hoverMetadata}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {showRemoveButton ? (
          <Tooltip title="Remove file">
            <IconButton
              size="small"
              aria-label={`remove-file-${filename}`}
              onClick={(event) => {
                event.stopPropagation();
                onRemove?.();
              }}
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                p: 0.25,
                bgcolor: alpha(theme.palette.background.paper, 0.92),
                "&:hover": {
                  bgcolor: "background.paper",
                },
              }}
            >
              <ClearIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
      {footer}
      {resolvedPreviewFileUrl ? (
        <FullScreenDocumentPreview
          open={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          fileUrl={resolvedPreviewFileUrl}
          fileExtension={fileExtension}
          title={filename}
        />
      ) : null}
    </Stack>
  );
}
